import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { absencesService } from "../services/absences.service";
import type { Absence, AbsenceCreateInput, AbsenceReviewAction } from "../types";

export interface AbsenceFilters {
	status?: string;
	type?: string;
	employeeId?: string;
	from?: string;
	to?: string;
}

export function useAbsences(filters: AbsenceFilters) {
	// ⚠️ Bo'sh qiymatlar `params` ga QO'SHILMAYDI — ular `queryKey` ga
	// kiradi va `undefined` maydonlar keraksiz qayta so'rovlar berardi.
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(filters)) {
		if (value) {
			params[key] = value;
		}
	}

	return usePaginatedQuery<Absence>({ queryKey: ["absences"], url: "/absences", params });
}

/**
 * «Yangi ariza» modalidagi xodim tanlovi.
 *
 * ⚠️ `GET /employees` — SAHIFALANGAN resurs, konvert `data.items`
 * (`data` EMAS). `limit: 100` — modaldagi tanlov uchun yetarli; qidiruv
 * `q` parametri bilan serverda bajariladi, klientda emas.
 *
 * ⚠️ `attendance` modulidagi `useAttendanceEmployeeOptions` IMPORT
 * QILINMAYDI — modullararo hook importi ikki modulni bir-biriga
 * bog'lardi va `attendance` C blokining mulki.
 */
export function useAbsenceEmployeeOptions(search: string) {
	return useQuery({
		queryKey: ["employees", "absence-options", search],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };

			if (search.trim()) {
				params.q = search.trim();
			}

			const response = await apiClient.get<{
				success: true;
				data: { items: { id: string; fullName: string }[] };
			}>("/employees", { params });

			return response.data.data.items.map((item) => ({
				value: item.id,
				label: item.fullName,
			}));
		},
		staleTime: 30_000,
	});
}

export function useCreateAbsence() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: AbsenceCreateInput) => absencesService.create(input),
		onSuccess: () => {
			message.success("Ariza yaratildi");
			queryClient.invalidateQueries({ queryKey: ["absences"] });
		},
		onError: (error) => message.error(getErrorMessage(error, "Arizani yaratib bo'lmadi")),
	});
}

/**
 * Qaror mutatsiyasi.
 *
 * ⚠️ **Davomat kunlari ASINXRON qayta quriladi** — backend har kun uchun
 * `attendance` navbatiga job qo'yadi va u worker tomonidan bajariladi.
 * Shuning uchun `attendance-days` keshi ham invalidatsiya qilinadi;
 * darhol kelgan javob hali ESKI holatni ko'rsatishi mumkin va bu
 * NORMAL — `attendance` moduli o'z poll mexanizmiga ega
 * (`recompute.store.ts`).
 */
export function useReviewAbsence() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (vars: { id: string; action: AbsenceReviewAction; reviewNote?: string }) =>
			absencesService.review(vars.id, vars.action, vars.reviewNote),
		onSuccess: () => {
			message.success("Qaror saqlandi");
			queryClient.invalidateQueries({ queryKey: ["absences"] });
			queryClient.invalidateQueries({ queryKey: ["attendance-days"] });
		},
		onError: (error) => message.error(getErrorMessage(error, "Qarorni saqlab bo'lmadi")),
	});
}
